import { createApi } from '@reduxjs/toolkit/query/react';
import { formatNameFirstLast, sortAsc } from '@strata/helper-functions';

import { REFERENCE_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export interface Payer {
    payerId: string;
    name: string;
    secondaryData: string;
}

export interface PayerSegment {
    segmentId: string;
    payerId: string;
    name: string;
    secondaryData: string;
}

export const referenceApi = createApi({
    baseQuery: getBaseQuery(REFERENCE_API_URL),
    reducerPath: 'referenceApi',
    endpoints: (build) => ({
        getPayerList: build.query<Payer[], void>({
            query: () => 'payers/',
            transformResponse: (response: { payer_id: string; payer_name: string }[]) =>
                response
                    ?.map((payer) => ({
                        payerId: payer.payer_id,
                        name: payer.payer_name,
                        secondaryData: payer.payer_id,
                    }))
                    .sort((a, b) => sortAsc(a.name, b.name)),
            transformErrorResponse: handleRTKQueryError('An error occurred fetching payers.'),
        }),
        getPayerSegmentList: build.query<PayerSegment[], void>({
            query: () => 'payer_segments',
            transformResponse: (
                response: { segment_id: string; payer_id: string; segment_name: string }[],
            ) =>
                response
                    ?.map((segment) => ({
                        segmentId: segment.segment_id,
                        payerId: segment.payer_id,
                        name: segment.segment_name,
                        secondaryData: segment.segment_id,
                    }))
                    .sort((a, b) => sortAsc(a.segmentId, b.segmentId)),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred fetching payer segments.',
            ),
        }),
        getPlaceOfServiceOptions: build.query<
            { code: string; description: string; name: string; secondaryData: string }[],
            void
        >({
            query: () => 'product_benefit_configs/places',
            transformResponse: (response: { code: string; description: string }[]) =>
                response
                    ?.map((placeOfService) => ({
                        code: placeOfService.code,
                        description: placeOfService.description,
                        name: placeOfService.description,
                        secondaryData: placeOfService.code,
                    }))
                    .sort((a, b) => sortAsc(a.name, b.name)),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred fetching place of service options.',
            ),
        }),
        getSupplierList: build.query<{ name: string; secondaryData: string; npi: string }[], void>({
            query: () => 'suppliers/parents',
            transformResponse: (response: { supplier_npi: string; organization_name: string }[]) =>
                response
                    ?.map((supplier) => ({
                        name: supplier.organization_name,
                        secondaryData: supplier.supplier_npi,
                        npi: supplier.supplier_npi,
                    }))
                    .sort((a, b) => sortAsc(a.name, b.name)),
            transformErrorResponse: handleRTKQueryError('An error occurred fetching suppliers.'),
        }),
        getPrescriberList: build.query<
            { prescriberNpi: string; name: string; secondaryData: string }[],
            void
        >({
            query: () => '/prescribers/',
            transformResponse: (
                response: {
                    prescriber_npi: string;
                    first_name: string;
                    last_name: string;
                }[],
            ) =>
                response
                    ?.map((prescriber) => ({
                        prescriberNpi: prescriber.prescriber_npi,
                        name: formatNameFirstLast(prescriber.first_name, prescriber.last_name),
                        secondaryData: prescriber.prescriber_npi,
                    }))
                    .sort((a, b) => sortAsc(a.prescriberNpi, b.prescriberNpi)),
            transformErrorResponse: handleRTKQueryError('An error occurred fetching prescribers.'),
        }),
    }),
});

export const {
    useGetPayerListQuery,
    useGetPayerSegmentListQuery,
    useGetPlaceOfServiceOptionsQuery,
    useGetSupplierListQuery,
    useGetPrescriberListQuery,
} = referenceApi;
